import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'

export interface AssistantContextItem {
  type: 'task' | 'order' | 'sdr' | 'note' | 'appointment' | 'custom'
  resourceType: string
  resourceId: string
  label?: string
}

export const useAIAssistantStore = create(
  devtools(
    persist(
      combine(
        {
          open: false,
          patientId: null as string | null,
          context: [] as AssistantContextItem[],
          resourceRef: null as string | null,
          initialPrompt: null as string | null,
          /* True while an AI chat generation is actively streaming/staging.
           * Mirrored here from the AI chat provider (whose own streaming state
           * is scoped to the assistant panel's subtree) so SIBLING surfaces —
           * e.g. the visit-note editor page, which lives in a different panel —
           * can react to it. Read by the navigate-away guard so leaving the
           * editor mid-generation prompts before silently aborting the run.
           * NOT persisted (see `partialize`): it's live session state. */
          isGenerating: false,
        },
        (set, get) => ({
          setOpen: (open: boolean) => set({ open }),
          toggleOpen: () => set({ open: !get().open }),
          setPatientId: (patientId: string | null) => set({ patientId }),
          setGenerating: (isGenerating: boolean) => set({ isGenerating }),
          pushContext: (item: AssistantContextItem) =>
            set({
              context: [
                ...get().context.filter(
                  (c) => c.resourceId !== item.resourceId
                ),
                item,
              ],
            }),
          removeContext: (resourceId: string) =>
            set({
              context: get().context.filter((c) => c.resourceId !== resourceId),
            }),
          clearContext: () => set({ context: [] }),
          setResourceContext: (resourceRef: string) => set({ resourceRef }),
          clearResourceContext: () => set({ resourceRef: null }),
          setInitialPrompt: (initialPrompt: string) => set({ initialPrompt }),
          clearInitialPrompt: () => set({ initialPrompt: null }),
        })
      ),
      {
        name: 'ai-assistant',
        partialize: (state) => ({
          open: state.open,
        }),
      }
    )
  )
)
